import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getDevis, getDevisById, createDevis, updateDevis, deleteDevis, getClients, getArticles, viewDevisPDF, downloadDevisPDF, envoyerDevisEmail } from '../api';
import StatusBadge from './StatusBadge';
import SearchSelect from './SearchSelect';
import ConfirmModal from './ConfirmModal';

const TAUX_TVA_OPTIONS = [
  { value: '0', label: '0 % — Exonéré' },
  { value: '2.1', label: '2,1 % — Super-réduit' },
  { value: '5.5', label: '5,5 % — Réduit' },
  { value: '10', label: '10 % — Intermédiaire' },
  { value: '20', label: '20 % — Normal' },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = () => ({
  client_id: '',
  date_creation: today(),
  date_validite: '',
  statut: 'brouillon',
  notes: '',
  lignes: [{ description: '', quantite: 1, prix_unitaire: 0, article_id: '', tva: 20, position: 0 }],
});

function Devis() {
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailDevis, setDetailDevis] = useState(null);
  const [editingDevis, setEditingDevis] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM());
  const [alert, setAlert] = useState(null);
  const [working, setWorking] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [emailModal, setEmailModal] = useState(null);
  const [emailForm, setEmailForm] = useState({ email: '', message: '' });
  const dragIndex = useRef(null);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [dRes, cRes, aRes] = await Promise.all([getDevis(), getClients(), getArticles()]);
      setDevis(dRes.data);
      setClients(cRes.data);
      setArticles(aRes.data);
    } catch {
      showAlert('Erreur lors du chargement', 'error');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingDevis(null);
    setFormData(EMPTY_FORM());
  };

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index] = { ...newLignes[index], [field]: value };
    if (field === 'article_id' && value) {
      const art = articles.find(a => a.id === parseInt(value));
      if (art) {
        newLignes[index].prix_unitaire = art.prix_unitaire;
        newLignes[index].tva = art.taux_tva != null ? art.taux_tva : 20;
        newLignes[index].total_ht = (parseFloat(newLignes[index].quantite) || 0) * art.prix_unitaire;
      }
    }
    if (field === 'quantite' || field === 'prix_unitaire') {
      const q = parseFloat(newLignes[index].quantite) || 0;
      const p = parseFloat(newLignes[index].prix_unitaire) || 0;
      newLignes[index].total_ht = q * p;
    }
    setFormData({ ...formData, lignes: newLignes });
  };

  const handleEdit = async (id) => {
    try {
      const res = await getDevisById(id);
      const d = res.data;
      setEditingDevis(d);
      setFormData({
        client_id: String(d.client_id || ''),
        date_creation: d.date_creation || today(),
        date_validite: d.date_validite || '',
        statut: d.statut || 'brouillon',
        notes: d.notes || '',
        lignes: (d.lignes && d.lignes.length) ? d.lignes.map((l, idx) => ({
          description: l.description || l.article_nom || '',
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          article_id: l.article_id ? String(l.article_id) : '',
          tva: l.tva != null ? l.tva : 20,
          total_ht: l.total_ht,
          position: l.position ?? idx,
        })) : [{ description: '', quantite: 1, prix_unitaire: 0, article_id: '', tva: 20, total_ht: 0, position: 0 }],
      });
      setShowModal(true);
    } catch {
      showAlert('Erreur lors du chargement du devis', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWorking('save');
    try {
      const lignes = formData.lignes.map((l, idx) => ({
        ...l,
        quantite: parseFloat(l.quantite) || 0,
        prix_unitaire: parseFloat(l.prix_unitaire) || 0,
        tva: parseFloat(l.tva) != null && !isNaN(parseFloat(l.tva)) ? parseFloat(l.tva) : 20,
        total_ht: (parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0),
        position: idx,
      }));
      const taux_tva = lignes.length > 0 ? lignes[0].tva : 20;
      const payload = {
        client_id: parseInt(formData.client_id),
        date_creation: formData.date_creation || today(),
        date_validite: formData.date_validite || '',
        taux_tva,
        statut: formData.statut || 'brouillon',
        notes: formData.notes || '',
        lignes,
      };
      if (editingDevis) {
        await updateDevis(editingDevis.id, payload);
        showAlert('Devis modifié avec succès', 'success');
      } else {
        await createDevis(payload);
        showAlert('Devis créé avec succès', 'success');
      }
      closeModal();
      loadAll();
    } catch {
      showAlert(editingDevis ? 'Erreur lors de la modification' : 'Erreur lors de la création', 'error');
    } finally {
      setWorking(null);
    }
  };

  const handleDelete = (d) => {
    setConfirm({
      title: 'Supprimer le devis',
      message: 'Ce devis sera définitivement supprimé. Cette action est irréversible.',
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        setWorking(d.id + '_delete');
        try {
          await deleteDevis(d.id);
          showAlert('Devis supprimé', 'success');
          loadAll();
        } catch {
          showAlert('Erreur lors de la suppression', 'error');
        } finally {
          setWorking(null);
        }
      },
    });
  };

  const openEmailModal = (d) => {
    setEmailModal(d);
    setEmailForm({ email: d.client_email || '', message: '' });
  };

  const closeEmailModal = () => { setEmailModal(null); setEmailForm({ email: '', message: '' }); };

  const handleEnvoyerEmailSubmit = async (e) => {
    e.preventDefault();
    setWorking(`${emailModal.id}_email`);
    try {
      const res = await envoyerDevisEmail(emailModal.id, emailForm.email, emailForm.message);
      showAlert(res.data.message || 'Devis envoyé', 'success');
      closeEmailModal();
    } catch (err) {
      showAlert(err.response?.data?.error || "Erreur lors de l'envoi", 'error');
    } finally {
      setWorking(null);
    }
  };

  const handleShowDetail = async (id) => {
    try {
      const res = await getDevisById(id);
      setDetailDevis(res.data);
      setShowDetailModal(true);
    } catch {
      showAlert('Erreur lors du chargement du devis', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Devis</h2>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Nouveau devis</button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Numéro</th>
              <th>Client</th>
              <th>Date</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devis.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center' }}>Aucun devis</td></tr>
            ) : devis.map(d => (
              <tr key={d.id}>
                <td className="num">{d.numero}</td>
                <td>{d.client_nom}</td>
                <td>{new Date(d.date_creation + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                <td className="amount">{Number(d.total_ttc || 0).toFixed(2)} €</td>
                <td><StatusBadge statut={d.statut} /></td>
                <td>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openDropdown === d.id) { setOpenDropdown(null); return; }
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDropdownPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                      setOpenDropdown(d.id);
                    }}
                  >
                    Actions ▾
                  </button>
                  {openDropdown === d.id && (
                    <div className="dropdown-menu" style={{ top: dropdownPos.top, right: dropdownPos.right }} onClick={(e) => e.stopPropagation()}>
                      <button className="dropdown-item" onClick={() => { handleShowDetail(d.id); setOpenDropdown(null); }}>
                        Voir le détail
                      </button>
                      <button className="dropdown-item" onClick={() => { handleEdit(d.id); setOpenDropdown(null); }}>
                        Modifier
                      </button>
                      <div className="dropdown-divider" />
                      <button className="dropdown-item" onClick={() => { viewDevisPDF(d.id); setOpenDropdown(null); }}>
                        Voir le PDF
                      </button>
                      <button className="dropdown-item" onClick={() => { downloadDevisPDF(d.id); setOpenDropdown(null); }}>
                        Télécharger PDF
                      </button>
                      <button className="dropdown-item" onClick={() => { openEmailModal(d); setOpenDropdown(null); }}>
                        Envoyer par email
                      </button>
                      <div className="dropdown-divider" />
                      <button className="dropdown-item dropdown-item-danger" onClick={() => { handleDelete(d); setOpenDropdown(null); }} disabled={!!working}>
                        Supprimer
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDetailModal && detailDevis && (
        <div className="modal" onClick={() => { setShowDetailModal(false); setDetailDevis(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860 }}>
            <div className="modal-header">
              <h2>Détail — {detailDevis.numero}</h2>
              <button className="close-btn" onClick={() => { setShowDetailModal(false); setDetailDevis(null); }}>×</button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <p><strong>Client :</strong> {detailDevis.client_nom}</p>
              <p><strong>Date de création :</strong> {new Date(detailDevis.date_creation + 'T00:00:00').toLocaleDateString('fr-FR')}</p>
              {detailDevis.date_validite && <p><strong>Date de validité :</strong> {new Date(detailDevis.date_validite + 'T00:00:00').toLocaleDateString('fr-FR')}</p>}
              <p><strong>Statut :</strong> <StatusBadge statut={detailDevis.statut} /></p>
              <p><strong>Total TTC :</strong> {Number(detailDevis.total_ttc || 0).toFixed(2)} €</p>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Qté</th>
                  <th>Prix unit. (€)</th>
                  <th>TVA %</th>
                  <th>Total HT (€)</th>
                </tr>
              </thead>
              <tbody>
                {(detailDevis.lignes && detailDevis.lignes.length) ? detailDevis.lignes.map((ligne, idx) => (
                  <tr key={idx}>
                    <td style={{ whiteSpace: 'pre-wrap' }}>{ligne.description || ligne.article_nom || '—'}</td>
                    <td>{ligne.quantite}</td>
                    <td>{ligne.prix_unitaire != null ? Number(ligne.prix_unitaire).toFixed(2) : '—'}</td>
                    <td>{ligne.tva != null ? Number(ligne.tva) : '—'}</td>
                    <td>{ligne.total_ht != null ? Number(ligne.total_ht).toFixed(2) : '—'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" style={{ textAlign: 'center' }}>Aucune ligne</td></tr>
                )}
              </tbody>
            </table>
            <div className="actions" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowDetailModal(false); setDetailDevis(null); }}>Fermer</button>
              <button type="button" className="btn btn-primary" onClick={() => { setShowDetailModal(false); handleEdit(detailDevis.id); setDetailDevis(null); }}>Modifier</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 860 }}>
            <div className="modal-header">
              <h2>{editingDevis ? 'Modifier le devis' : 'Nouveau devis'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Client *</label>
                <SearchSelect
                  required
                  placeholder="Rechercher un client…"
                  options={clients.map(c => ({ value: c.id, label: c.nom }))}
                  value={formData.client_id}
                  onChange={(val) => setFormData({ ...formData, client_id: val })}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date de création *</label>
                  <input type="date" required value={formData.date_creation}
                    onChange={(e) => setFormData({ ...formData, date_creation: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date de validité</label>
                  <input type="date" value={formData.date_validite}
                    onChange={(e) => setFormData({ ...formData, date_validite: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Statut</label>
                  <select value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })}>
                    <option value="brouillon">Brouillon</option>
                    <option value="envoye">Envoyé</option>
                    <option value="accepte">Accepté</option>
                    <option value="refuse">Refusé</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Lignes</label>
                {formData.lignes.map((ligne, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => { dragIndex.current = index; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIndex.current;
                      if (from === null || from === index) return;
                      const newLignes = [...formData.lignes];
                      const [moved] = newLignes.splice(from, 1);
                      newLignes.splice(index, 0, moved);
                      dragIndex.current = null;
                      setFormData({ ...formData, lignes: newLignes });
                    }}
                    style={{ border: '1px solid var(--border-color)', padding: 12, marginBottom: 8, borderRadius: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <span style={{ cursor: 'grab', color: 'var(--color-text-muted)', fontSize: 16, userSelect: 'none', lineHeight: 1 }} title="Glisser pour réordonner">⠿</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>Ligne {index + 1}</span>
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>Article</label>
                      <SearchSelect
                        placeholder="Article du catalogue…"
                        options={articles.map(a => ({ value: a.id, label: `${a.nom} — ${a.prix_unitaire.toFixed(2)} €` }))}
                        value={ligne.article_id}
                        onChange={(val) => handleLigneChange(index, 'article_id', val)}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label>Description</label>
                      <textarea
                        rows={2}
                        value={ligne.description}
                        onChange={(e) => handleLigneChange(index, 'description', e.target.value)}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Qté</label>
                        <input type="number" step="0.01" value={ligne.quantite}
                          onChange={(e) => handleLigneChange(index, 'quantite', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Prix unit. (€)</label>
                        <input type="number" step="0.01" value={ligne.prix_unitaire}
                          onChange={(e) => handleLigneChange(index, 'prix_unitaire', e.target.value)} />
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>TVA</label>
                        <select value={String(ligne.tva)}
                          onChange={(e) => handleLigneChange(index, 'tva', parseFloat(e.target.value))}>
                          {TAUX_TVA_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Total HT</label>
                        <input type="text" readOnly
                          value={`${((parseFloat(ligne.quantite) || 0) * (parseFloat(ligne.prix_unitaire) || 0)).toFixed(2)} €`} />
                      </div>
                      <button type="button" className="btn btn-danger btn-sm"
                        onClick={() => setFormData({ ...formData, lignes: formData.lignes.filter((_, i) => i !== index) })}>
                        ×
                      </button>
                    </div>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary"
                  onClick={() => setFormData({ ...formData, lignes: [...formData.lignes, { description: '', quantite: 1, prix_unitaire: 0, article_id: '', tva: 20, position: formData.lignes.length }] })}>
                  + Ajouter une ligne
                </button>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea rows={2} value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={working === 'save'}>
                  {working === 'save' ? 'Sauvegarde…' : (editingDevis ? 'Modifier' : 'Créer le devis')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {emailModal && (
        <div className="modal" onClick={closeEmailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Envoyer le devis — {emailModal.numero}</h2>
              <button className="close-btn" onClick={closeEmailModal}>×</button>
            </div>
            <form onSubmit={handleEnvoyerEmailSubmit}>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label className="form-label">Destinataire</label>
                  <input
                    className="form-input"
                    type="email"
                    required
                    value={emailForm.email}
                    onChange={(e) => setEmailForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@client.fr"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Message (optionnel)</label>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={emailForm.message}
                    onChange={(e) => setEmailForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="Bonjour, veuillez trouver ci-joint votre devis…"
                    style={{ resize: 'vertical' }}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-slate-400)' }}>
                    Ce message apparaîtra dans le corps de l'email, avant le PDF joint.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeEmailModal}>Annuler</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={working === `${emailModal.id}_email`}
                >
                  {working === `${emailModal.id}_email` ? 'Envoi en cours…' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'danger'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default Devis;
